// FILE: src/services/learnerProfileService.js
//
// CRUD for learner_profiles (migration 017). RLS ensures a learner can only
// see/modify their own row, so these helpers are simple — no admin/learner
// branching needed.
//
// Phase A only touches upsertLearnerProfile (called on first sign-in) and
// getLearnerProfile (called when the dashboard mounts). Later phases add
// helpers for target_exam_date, recommended_exam_date, etc.

import { supabase } from './supabaseClient';

/**
 * Idempotent insert. On first sign-in, creates the row. On subsequent
 * sign-ins, the ON CONFLICT clause leaves any user-set fields alone.
 *
 * @param {object} params
 * @param {string} params.userId       - auth.users.id
 * @param {string} [params.displayName]
 * @param {string} [params.timezone]   - IANA tz string
 * @returns {Promise<object|null>} the row, or null on failure
 */
export async function upsertLearnerProfile({ userId, displayName, timezone }) {
  if (!userId) {
    console.warn('[learnerProfileService] upsertLearnerProfile called without userId');
    return null;
  }

  const row = {
    user_id:      userId,
    display_name: displayName || null,
    timezone:     timezone || null,
  };

  try {
    const { data, error } = await supabase
      .from('learner_profiles')
      .upsert(row, { onConflict: 'user_id', ignoreDuplicates: true })
      .select()
      .maybeSingle();

    if (error) {
      console.warn('[learnerProfileService] upsert failed:', error.message);
      return null;
    }

    // If ignoreDuplicates skipped the insert, fetch the existing row so the
    // caller still receives a profile snapshot.
    if (!data) {
      return await getLearnerProfile(userId);
    }
    return data;
  } catch (err) {
    console.warn('[learnerProfileService] unexpected upsert error:', err?.message || err);
    return null;
  }
}

/**
 * Fetch the current learner's profile row. Returns null if not yet created
 * or if RLS denies the read (i.e. caller isn't the row owner).
 *
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
export async function getLearnerProfile(userId) {
  if (!userId) return null;

  try {
    const { data, error } = await supabase
      .from('learner_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.warn('[learnerProfileService] select failed:', error.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.warn('[learnerProfileService] unexpected select error:', err?.message || err);
    return null;
  }
}

/**
 * Patch a profile row. Caller passes the column names already in snake_case
 * matching the migration.
 *
 * @param {string} userId
 * @param {object} patch
 * @returns {Promise<object|null>}
 */
export async function updateLearnerProfile(userId, patch) {
  if (!userId || !patch || typeof patch !== 'object') return null;

  try {
    // Upsert (not update): a learner who hasn't set a baseline yet may not have
    // a profile row, in which case a plain .update() matches 0 rows and silently
    // no-ops (the "Set my exam date did nothing" bug). Upserting on user_id
    // creates the row if missing and updates it otherwise.
    const { data, error } = await supabase
      .from('learner_profiles')
      .upsert({ user_id: userId, ...patch }, { onConflict: 'user_id' })
      .select()
      .maybeSingle();

    if (error) {
      console.warn('[learnerProfileService] update failed:', error.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.warn('[learnerProfileService] unexpected update error:', err?.message || err);
    return null;
  }
}
